import { Link, useNavigate } from 'react-router';
import styled from 'styled-components';

import Button from 'react-bootstrap/Button';
import { Button as AntdButton, Dropdown as AntdDropdown } from 'antd';
import { FaLocationDot } from 'react-icons/fa6';
import { AiOutlineMore, AiOutlineHistory } from 'react-icons/ai';

import ProjectTagsWrapper from '@components/projectComponents/projectTags/ProjectTagsWrapper';
import ProjectThumbnail from '@components/projectComponents/ProjectThumbnail';
import { CardTitle, CardDate } from '../ProjectCardComponents';
import { getFeatureByUuid } from '../utils/helpers';
import { buildProjectUrl, projectHomeUrl } from '@utils/projectPath';
import { IMAGE_MODE, MODEL_VIEW_MODE_ENABLED, PROJECT_VIEW_MODE } from '@utils/constants';

const StyledListItem = styled.div`
	display: grid;
	grid-template-columns: 8rem minmax(14rem, 100vw);
	${props => props.$error && 'grid-template-columns: minmax(auto, 100vw);'}
	grid-template-rows: auto;
	grid-auto-flow: column;

	height: max-content;
	background: ${props => props.theme.colors.modal};
	border-radius: 0.375rem;
	position: relative;

	overflow: hidden;
`;

const ProjectTags = styled(ProjectTagsWrapper)`
	/* Styles tag */
	ul li span.badge {
		padding: 0.125rem 0.25rem !important;
	}
`;

const Body = styled.div`
	display: grid;
	grid-template-rows: min-content min-content;
	${props => props.$error && 'background: var(--bs-danger-bg-subtle);'}

	padding: 1rem 1.25rem;
	padding-top: 1.5rem;
`;

const StyledCardTitle = styled(CardTitle)`
	/* Truncates Overflowing Text */
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const ButtonWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 0.5rem;
	margin-top: 1.5rem;

	/* Attaches button wrapper to right side for larger screens */
	@media (min-width: 2000px) {
		justify-self: end;
	}
	@media (max-width: 991.98px) {
		justify-self: end;
	}
`;

const ZoomToPinButton = styled(Button)`
	justify-self: start;
`;

const StyledError = styled.p`
	margin: 0;
	margin-top: 1.5rem;
`;

const ActionToggleButton = styled(AntdButton)`
	&&& {
		width: 36px;
		height: 36px;
		display: flex;
		justify-content: center;
		align-items: center;
		background-color: ${p => p.theme.colors.border};
		font-size: 1.5rem;
	}
`;

const ListItemDropdown = ({ uuid }) => {
	const navigate = useNavigate();

	// The model-mode link is the menu's only item, so the whole menu goes with it.
	if (!MODEL_VIEW_MODE_ENABLED) return null;

	return (
		<AntdDropdown
			menu={{
				items: [
					{
						key: 'view-model-mode',
						label: 'View in model-based mode',
						icon: <AiOutlineHistory aria-hidden="true" />,
						onClick: () => navigate(buildProjectUrl(uuid, PROJECT_VIEW_MODE.MODEL)),
					},
				],
			}}
			trigger={['click']}
			placement="bottomRight">
			<ActionToggleButton
				shape="circle"
				type="text"
				icon={<AiOutlineMore aria-hidden="true" />}
				aria-label="Toggle project actions"
			/>
		</AntdDropdown>
	);
};

const ListItem = ({ projectData, map }) => {
	if (!projectData || !map) return null;
	const { orthophoto_progress, uuid, tags, title, created_at, image_mode } = projectData;
	const error = !!orthophoto_progress?.error;

	const isVideoProject = image_mode === IMAGE_MODE.VIDEO;
	const projectPath = projectHomeUrl(uuid, image_mode);

	const zoomToProject = () => {
		if (!uuid) return;
		const feature = getFeatureByUuid(map, 'Cluster Pin Layer', uuid);
		if (!feature) return;

		const view = map.getView();
		view.setCenter(feature.get('data').center_coordinate);
		view.setZoom(20);
	};

	return (
		<StyledListItem $error={error}>
			<ProjectTags tags={tags} uuid={uuid} />
			{!error && <ProjectThumbnail uuid={uuid} projectPath={projectPath} />}
			<Body $error={error}>
				<StyledCardTitle> {title} </StyledCardTitle>
				<CardDate created_at={created_at} />
				{error ? (
					<StyledError className="alert alert-danger small p-2">
						Project processing has failed
					</StyledError>
				) : (
					<ButtonWrapper>
						<Button to={projectPath} as={Link} variant="dark">
							View Project
						</Button>
						<ZoomToPinButton onClick={zoomToProject} variant="dark">
							<FaLocationDot />
						</ZoomToPinButton>
						{!isVideoProject && <ListItemDropdown uuid={uuid} />}
					</ButtonWrapper>
				)}
			</Body>
		</StyledListItem>
	);
};
export default ListItem;
